'use client';

import { datadogRum } from '@newfront-insurance/dd-rum';
import type { ReactNode } from 'react';
import { useTimeoutFn } from 'react-use';

export const STUCK_LOADING_REPORT_MS = 30_000;

/**
 * A preview that never finishes loading shows no error, so loading states report it as a RUM error once
 * STUCK_LOADING_REPORT_MS passes. Error Tracking groups these into one issue, which the browser error monitor alerts on.
 */
export function reportStuckLoading() {
  datadogRum.addError(new Error('Artifact preview still loading after 30 seconds'), { source: 'artifact-preview' });
}

function PreviewMessage({ children }: { children: ReactNode }) {
  return (
    <div className="text-muted-foreground flex h-full min-h-48 items-center justify-center p-6 text-sm">{children}</div>
  );
}

/** Loading state of an artifact preview. */
export function PreviewStatus({ children }: { children: ReactNode }) {
  useTimeoutFn(reportStuckLoading, STUCK_LOADING_REPORT_MS);

  return <PreviewMessage>{children}</PreviewMessage>;
}

export function PreviewError({ message }: { message: string }) {
  return (
    <PreviewMessage>
      <div className="max-w-md text-center">
        <div className="text-primary font-medium">Preview failed to load.</div>
        <div className="mt-1 break-words">{message}</div>
      </div>
    </PreviewMessage>
  );
}
